import { usePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The monthly cap on Workspace detail, editable (plan-014 E3b; FR12-ENT-06). B3 drew the cap read-only with a link; this
 * is the line it shows and, for a person whose role IN this workspace holds `spend_cap.write` (Owner, Admin), the same
 * cap form Finance → Allowances and caps uses.
 *
 * THE REVISION A CHANGE NAMES comes from the workspace's own read (INT 2026-10-07): `GET /v1/workspaces/{id}` carries
 * the full spend cap — amount, behaviour at it, overage, warning and revision — so a workspace Owner or Admin without
 * an organization finance role edits an existing cap here too (`0` creates one where there is none).
 */
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { MemberRole } from "@wetalk/domain";
import { Button } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { QUERY_KEY } from "../../../api/query.js";
import type { SpendCapView } from "../../../api/slice/entitlement.js";
import { roleHolds, useOpenAccount } from "../../account-admin/overview/account-overview-screen.js";
import { capSummaryOf } from "./allowance-copy.js";
import { mayChangeCap } from "./allowance-screen.js";
import { SpendCapForm } from "./spend-cap-form.js";

import "./allowance.css";

export interface WorkspaceSpendCapProps {
  readonly workspaceId: string;
  readonly workspaceName: string;
  /** The person's role IN this workspace (`null`: none — an organization role sees it without one). */
  readonly memberRole: MemberRole | null;
  /** The full cap as the workspace's own read carries it, or `null` for none. */
  readonly cap: SpendCapView | null;
  /** Whether this is the workspace the person is working in (Billing changes only that one). */
  readonly isCurrent: boolean;
}

export function WorkspaceSpendCap(props: WorkspaceSpendCapProps): ReactNode {
  usePortalTranslation("finance");
  const queryClient = useQueryClient();
  const open = useOpenAccount();
  const canReadFinance = roleHolds(open.accountRole, "account.finance.read");
  const mayWrite = mayChangeCap(props.memberRole);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = usePortalMessage();
  const current = props.cap;

  const reread = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["entitlements"] });
    void queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.workspace, props.workspaceId, "management"] });
  };

  return (
    <div className="al-ws-cap">
      <p className="c-ws-fact-dek">
        {capSummaryOf(current)}{" "}
        {!mayWrite && props.isCurrent ? (
          <Link to="/billing" className="c-ws-fact-link">{portalText("finance", "m0421")}</Link>
        ) : null}
      </p>
      {saved === null ? null : (
        <p className="c-ws-fact-dek" role="status">
          {saved}
        </p>
      )}
      {mayWrite && !editing ? (
        <Button
          variant="ghost"
          onClick={() => {
            setSaved(null);
            setEditing(true);
          }}
        >
          {current === null ? portalText("finance", "m0422") : portalText("finance", "m0423")}
        </Button>
      ) : null}
      {mayWrite && editing ? (
        <SpendCapForm
          key={`${props.workspaceId}-${current === null ? 0 : current.revision}`}
          workspaceId={props.workspaceId}
          workspaceName={props.workspaceName}
          current={current}
          onStale={reread}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setSaved(() => (portalText("finance", "m0424")));
            reread();
          }}
        />
      ) : null}
      {canReadFinance ? (
        <p className="c-ws-fact-dek">{portalText("finance", "m0425")}{" "}
          <Link to="/finance/allowances" search={{ workspace: props.workspaceId }} className="c-ws-fact-link">{portalText("finance", "m0426")}</Link>
          .
        </p>
      ) : null}
    </div>
  );
}
